import { useContext, useState } from "react"
import FloatingLabel from "react-bootstrap/FloatingLabel"
import Form from "react-bootstrap/Form"
import OverlayTrigger from "react-bootstrap/OverlayTrigger"
import Popover from "react-bootstrap/Popover"

import { CHAPERON_API_URL } from "../const/paths"
import VicinityContext from "../context/vicinityContext"
import { colorCode, riskProfile, safetyPercentage } from "../utilities/helpers"
import { submitNewReport } from "../services/chaperonConnector"
import { ToastNoPlace } from "./ToastNotification"
import { consoleLog } from "../utilities/helpers"

// reports.type, repurposed (2026-08-30) from an unused report-source
// classifier (every row was "user") into the category the report is
// about, matching the Analysis sidebar's category list 1:1 (see
// CardSafetyAnalysis for the canonical order/labels).
const CATEGORY_OPTIONS = [
    { value: "stability", label: "Stability" },
    { value: "anti-crime", label: "Anti-Crime" },
    { value: "cultural", label: "Cultural" },
    { value: "air-quality", label: "Air Quality" },
    { value: "medical-care", label: "Medical Care" },
    { value: "transportation", label: "Transportation" },
    { value: "weather", label: "Weather" },
    { value: "environmental", label: "Environmental" },
    { value: "personal", label: "Personal" },
]
const DEFAULT_CATEGORY = "personal"

// reports.emergency options. "" represents "not an emergency" (the
// default) rather than a dedicated string, so it round-trips as the same
// null the field already used when no radio button was checked.
const EMERGENCY_OPTIONS = [
    { value: "", label: "Not an emergency" },
    { value: "environmental", label: "Environmental" },
    { value: "violence", label: "Violence" },
    { value: "health", label: "Health" },
    { value: "fire", label: "Fire" },
    { value: "earthquake", label: "Earthquake" },
    { value: "weather", label: "Weather" },
    { value: "other", label: "Other" },
]

// Add new function to handle file upload to Google Cloud Storage
async function uploadFileToGCS(file) {
    try {
        // Create FormData object to send the file
        const formData = new FormData()
        formData.append("file", file)

        // Make POST request to your backend endpoint that handles GCS upload
        const token = localStorage.getItem("chaperon_auth_token")
        const response = await fetch(`${CHAPERON_API_URL}/v1/upload/reports`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        })

        if (!response.ok) {
            throw new Error("Failed to upload file")
        }

        const data = await response.json()
        consoleLog("Uploaded file data:", data)
        return data.url // Return the public URL of the uploaded file
    } catch (error) {
        console.error("Error uploading file:", error)
        throw error
    }
}

export function ReportModal({ onClose, reportPlace }) {
    const [slideValue, setSlideValue] = useState(reportPlace.overall_safety)
    const [riskClass, setRiskClass] = useState(
        colorCode(safetyPercentage(reportPlace.overall_safety))
    )
    const [safetyProfile, setSafetyProfile] = useState(
        riskProfile(safetyPercentage(reportPlace.overall_safety))
    )

    let toastMessage = useContext(VicinityContext).toastMessage
    let setToastMessage = useContext(VicinityContext).setToastMessage
    let toastOptions = useContext(VicinityContext).toastOptions
    let setToastOptions = useContext(VicinityContext).setToastOptions
    let setShowToast = useContext(VicinityContext).setShowToast
    let place = useContext(VicinityContext).place
    // consoleLog("Place in ReportModal:", place)
    // consoleLog("reportPlace", reportPlace)

    let popover = (
        <Popover id="popover-slider">
            <Popover.Header as="h3" className={`bg-${riskClass}`}>
                Safety level
            </Popover.Header>
            <Popover.Body>
                Assessment:{" "}
                <strong
                    className={`text-${riskClass}`}
                    style={{ textTransform: "capitalize" }}
                >
                    {safetyProfile}
                </strong>
                .
            </Popover.Body>
        </Popover>
    )

    const handleRangeChange = (event) => {
        const value = event.target.value
        setSlideValue(value)
        setRiskClass(colorCode(safetyPercentage(slideValue)))
        setSafetyProfile(riskProfile(safetyPercentage(slideValue)))
        document.getElementById("placeName").className = `bg-${riskClass}`
    }

    const handleReportSubmit = async (event) => {
        event.preventDefault()
        const title = document.getElementById("reportTitle").value
        const review = document.getElementById("reportReview").value
        const category = document.getElementById("reportCategory").value
        // "" means "Not an emergency" (the default option) -- normalize to
        // null so this round-trips the same as before the radio group was
        // replaced with a select.
        const selectedEmergencyOption =
            document.getElementById("reportEmergency").value || null

        // Attribute the report to the signed-in user, same as every other
        // screen that reads this key (dashboard.jsx, checker.jsx, etc.) --
        // stays null for a logged-out visitor, same as before.
        const storedUser = localStorage.getItem("chaperon_user_data")
        const user = storedUser ? JSON.parse(storedUser) : null
        const reporterName = user
            ? [user.first_name, user.last_name].filter(Boolean).join(" ").trim() ||
              user.username ||
              null
            : null

        const newReport = {
            entity_id: reportPlace.id,
            report_type: category,
            reporter_id: user?.id ?? null,
            reporter_name: reporterName,
            report_title: title,
            report_image: null,
            report_review: review,
            report_score: slideValue,
            report_emergency: selectedEmergencyOption,
        }

        consoleLog(newReport)
        const fileInput = document.getElementById("formFileSm")
        const file = fileInput.files[0]

        try {
            if (file) {
                const uploadedFileUrl = await uploadFileToGCS(file)
                newReport.report_image = uploadedFileUrl
            }
            await submitNewReport(newReport)

            toastMessage = `Report submitted successfully for ${reportPlace.name}`
            setToastMessage(toastMessage)
            setToastOptions({ ...toastOptions, icon: "grow" })

            setShowToast(true)
            onClose()
        } catch (error) {
            console.error("Error submitting report:", error)
            // TODO: Show error message to user
        }
    }

    return (
        <div className="modal-content">
            <ToastNoPlace />
            <div className="modal-header mb-3">
                <h4>
                    <i className="bi bi-plus-circle pe-2"></i>
                    Reviewing
                    {reportPlace?.name
                        ? ` ${reportPlace.name} (${reportPlace.country})`
                        : "..."}
                </h4>
            </div>
            <div className="modal-body pt-3">
                <form className="pb-3">
                    <label htmlFor="safetyScore" className="form-label">
                        How would you score the safety level for{" "}
                        <kbd
                            id="placeName"
                            className={`bg-${riskClass}`}
                            style={{ whiteSpace: "nowrap" }}
                        >{`${reportPlace.name}`}</kbd>{" "}
                        ?
                    </label>
                    <OverlayTrigger placement="right" overlay={popover}>
                        <input
                            type="range"
                            className="form-range"
                            min="1"
                            max="20"
                            step="0.5"
                            id="reportSafetyScoreSlider"
                            defaultValue={reportPlace.overall_safety}
                            onChange={handleRangeChange}
                        ></input>
                    </OverlayTrigger>
                    <FloatingLabel
                        controlId="reportTitle"
                        label="Give your report a title"
                        className="mb-3"
                    >
                        <Form.Control
                            type="text"
                            placeholder="Title"
                            maxLength="100"
                        />
                    </FloatingLabel>
                    <FloatingLabel
                        controlId="reportReview"
                        label="Share your safety report"
                    >
                        <Form.Control
                            as="textarea"
                            placeholder="Input your report here"
                            style={{ height: "100px" }}
                            maxLength="500"
                        />
                    </FloatingLabel>
                    <FloatingLabel
                        controlId="reportCategory"
                        label="Category"
                        className="mt-3"
                    >
                        <Form.Select defaultValue={DEFAULT_CATEGORY}>
                            {CATEGORY_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Form.Select>
                    </FloatingLabel>
                    <Form.Group controlId="formFileSm" className="mb-3 mt-3">
                        <Form.Label>
                            Upload a photo for report on {`${place.name}`}
                        </Form.Label>
                        <Form.Control type="file" size="sm" />
                    </Form.Group>
                    <FloatingLabel
                        controlId="reportEmergency"
                        label="Is there an ongoing emergency here?"
                        className="mt-3"
                    >
                        <Form.Select
                            defaultValue=""
                            title={`Select an option if there is currently an imminent risk affecting people in ${place.name}`}
                        >
                            {EMERGENCY_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Form.Select>
                    </FloatingLabel>
                </form>
            </div>
            <div className="modal-footer pt-3">
                <button
                    type="submit"
                    className="btn btn-dark me-3"
                    onClick={handleReportSubmit}
                >
                    Submit
                </button>
                <button type="submit" className="btn btn-secondary me-3">
                    Preview
                </button>
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={onClose}
                >
                    Close
                </button>
            </div>
        </div>
    )
}
